import React, { useEffect, useMemo, useRef, useState } from "react";

// Utility to create a gently curved line path
const createCurvedLinePath = (x1, y1, x2, y2) => {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const distance = Math.sqrt(dx * dx + dy * dy);

  // Arrow is 1/2 of the total distance, starting at 1/2 from origin
  const unitX = dx / distance;
  const unitY = dy / distance;

  // Start at 1/2 of the distance (midpoint)
  const startX = x1 + unitX * (distance / 2);
  const startY = y1 + unitY * (distance / 2);

  // End at destination (100% of the distance)
  const endX = x2;
  const endY = y2;

  // Create a gentle curve with a control point perpendicular to the line
  // Perpendicular offset (rotate unit vector 90 degrees)
  const perpX = -unitY;
  const perpY = unitX;

  // Random gentle curve offset (20-40px perpendicular to line)
  const curveAmount = 20 + Math.random() * 20;
  // Randomly choose which side to curve
  const curveSide = Math.random() > 0.5 ? 1 : -1;

  // Control point at the midpoint of the arrow with perpendicular offset
  const midX = (startX + endX) / 2;
  const midY = (startY + endY) / 2;
  const cpX = midX + perpX * curveAmount * curveSide;
  const cpY = midY + perpY * curveAmount * curveSide;

  return {
    path: `M${startX},${startY} Q${cpX},${cpY} ${endX},${endY}`,
    endX,
    endY,
    // Calculate the angle at the end of the line for the arrowhead
    // Use a point slightly before the end to get the direction
    angle: Math.atan2(endY - cpY, endX - cpX),
  };
};

// Create arrowhead path (simple > shape)
const createArrowheadPath = (x, y, angle) => {
  const arrowSize = 40;
  const arrowWidth = 26;

  // Calculate the two points of the arrowhead
  // Point 1: back and up
  const x1 =
    x -
    arrowSize * Math.cos(angle) +
    arrowWidth * Math.cos(angle + Math.PI / 2);
  const y1 =
    y -
    arrowSize * Math.sin(angle) +
    arrowWidth * Math.sin(angle + Math.PI / 2);

  // Point 2: back and down
  const x2 =
    x -
    arrowSize * Math.cos(angle) +
    arrowWidth * Math.cos(angle - Math.PI / 2);
  const y2 =
    y -
    arrowSize * Math.sin(angle) +
    arrowWidth * Math.sin(angle - Math.PI / 2);

  return `M${x1},${y1} L${x},${y} L${x2},${y2}`;
};

const AnimatedArrow = ({ line, isLatest }) => {
  const [pathLength, setPathLength] = useState(0);
  const [arrowLength, setArrowLength] = useState(0);
  const pathRef = useRef(null);
  const arrowRef = useRef(null);
  const [elapsed, setElapsed] = useState(0);
  const [fadeStartTime, setFadeStartTime] = useState(null);
  const [, forceUpdate] = useState(0);
  const wasLatest = useRef(isLatest);

  // Memoize the path data
  const pathData = useMemo(() => {
    return createCurvedLinePath(line.from.x, line.from.y, line.to.x, line.to.y);
  }, [line.from.x, line.from.y, line.to.x, line.to.y]);

  const arrowPath = useMemo(() => {
    return createArrowheadPath(pathData.endX, pathData.endY, pathData.angle);
  }, [pathData.endX, pathData.endY, pathData.angle]);

  useEffect(() => {
    if (pathRef.current) {
      const length = pathRef.current.getTotalLength();
      setPathLength(length);
    }
    if (arrowRef.current) {
      const length = arrowRef.current.getTotalLength();
      setArrowLength(length);
    }
  }, [pathData.path, arrowPath]);

  // Detect when this line becomes non-latest
  useEffect(() => {
    if (wasLatest.current && !isLatest) {
      // This line just became non-latest, start fading immediately
      setFadeStartTime(Date.now());
    }
    wasLatest.current = isLatest;
  }, [isLatest]);

  // Animate the fade when this line becomes non-latest
  useEffect(() => {
    if (!fadeStartTime) return;

    const fadeOutDuration = 500;
    const animate = () => {
      const fadeElapsed = Date.now() - fadeStartTime;
      if (fadeElapsed < fadeOutDuration) {
        requestAnimationFrame(animate);
        // Force re-render
        forceUpdate((n) => n + 1);
      }
    };

    requestAnimationFrame(animate);
  }, [fadeStartTime]);

  useEffect(() => {
    const startTime = Date.now();
    const animate = () => {
      const current = Date.now() - startTime;
      setElapsed(current);

      if (current < line.duration) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [line.duration]);

  // Animation phases:
  // 1. Drawing phase: line draws in from start to finish (800ms)
  // 2. Arrow drawing phase: arrowhead draws in (200ms)
  // 3. Full visibility: entire line + arrow stays visible (2000ms)
  // 4. Fade out: line + arrow disappears (500ms)
  const drawDuration = 800;
  const arrowDrawDuration = 200;
  const holdDuration = 2000;
  const fadeOutDuration = 500;

  let offset;
  let dasharray;
  let arrowOffset;
  let arrowDasharray;

  if (elapsed < drawDuration) {
    // Phase 1: Drawing animation - line draws in
    const drawProgress = elapsed / drawDuration;
    // Start with offset = pathLength (nothing visible), end at offset = 0 (fully visible)
    offset = pathLength - drawProgress * pathLength;
    dasharray = pathLength;
    // Arrow not visible yet
    arrowOffset = arrowLength;
    arrowDasharray = arrowLength;
  } else if (elapsed < drawDuration + arrowDrawDuration) {
    // Phase 2: Line is complete, arrow draws in
    offset = 0;
    dasharray = pathLength;
    const arrowProgress = (elapsed - drawDuration) / arrowDrawDuration;
    arrowOffset = arrowLength - arrowProgress * arrowLength;
    arrowDasharray = arrowLength;
  } else if (elapsed < drawDuration + arrowDrawDuration + holdDuration) {
    // Phase 3: Show entire line + arrow
    offset = 0;
    dasharray = pathLength;
    arrowOffset = 0;
    arrowDasharray = arrowLength;
  } else {
    // Phase 4: Fade out entire line + arrow
    offset = 0;
    dasharray = pathLength;
    arrowOffset = 0;
    arrowDasharray = arrowLength;
  }

  // Calculate opacity
  let opacity = 1;
  if (pathLength === 0 || arrowLength === 0) {
    // Hide until we have pathLength and arrowLength calculated (prevents initial flash)
    opacity = 0;
  } else {
    // Calculate natural fade progress
    let naturalOpacity = 1;
    if (elapsed >= drawDuration + arrowDrawDuration + holdDuration) {
      const fadeProgress =
        (elapsed - drawDuration - arrowDrawDuration - holdDuration) /
        fadeOutDuration;
      naturalOpacity = 1 - fadeProgress;
    }

    // Calculate immediate fade if not latest
    let immediateOpacity = 1;
    if (!isLatest && fadeStartTime) {
      const fadeElapsed = Date.now() - fadeStartTime;
      const fadeProgress = Math.min(fadeElapsed / fadeOutDuration, 1);
      immediateOpacity = 1 - fadeProgress;
    }

    // Use whichever is more faded (lower opacity)
    opacity = Math.min(naturalOpacity, immediateOpacity);
  }

  // Likely due to some rounding error, a dot of the arrowhead was painted
  // when the curved line was beginning to be drawn about every 1-in-8 times.
  // This does not prevent that dot from rendering as such, but it does
  // make it invisible.
  // We now guarantee the arrowhead being visible only after it has started
  // being drawn (delta > epsilon of 0.05).
  const arrowStrokeWidth = arrowDasharray - arrowOffset > 0.05 ? 10 : 0

  return (
    <>
      <path
        ref={pathRef}
        d={pathData.path}
        stroke={line.color}
        strokeWidth="10"
        fill="none"
        strokeDasharray={dasharray}
        strokeDashoffset={offset}
        strokeLinecap="round"
        style={{
          filter: "drop-shadow(1px 1px 1px rgba(0,0,0,0.3))",
          opacity: opacity,
        }}
      />
      <path
        ref={arrowRef}
        d={arrowPath}
        stroke={line.color}
        strokeWidth={arrowStrokeWidth}
        fill="none"
        strokeDasharray={arrowDasharray}
        strokeDashoffset={arrowOffset}
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{
          filter: "drop-shadow(1px 1px 1px rgba(0,0,0,0.3))",
          opacity: opacity,
        }}
      />
    </>
  );
};

export default AnimatedArrow;
